'use client'

import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Calendar as CalendarIcon, Apple, CreditCard, Lock, CheckCircle2, ArrowLeft, Loader2 } from "lucide-react";
import Image, { StaticImageData } from "next/image";
import { formatPrice } from "@/lib/formatters";
import confetti from "canvas-confetti";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export interface Stylist {
  name: string;
  specialty: string;
  image: string | StaticImageData;
  service: string;
  price: number;
}

interface Props {
  stylist: Stylist | null;
  onClose: () => void;
}

type Step = "selection" | "details" | "payment" | "success";

const days = Array.from({ length: 14 }).map((_, i) => {
  const d = new Date();
  d.setDate(d.getDate() + i);
  return d;
});

const slots = {
  Morning: ["09:00", "10:00", "11:00"],
  Afternoon: ["12:30", "14:00", "15:30"],
  Evening: ["17:00", "18:30", "20:00"],
};

export function BookingModal({ stylist, onClose }: Props) {
  const [step, setStep] = useState<Step>("selection");
  const [selectedDate, setSelectedDate] = useState<Date>(days[0]);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });

  useEffect(() => {
    if (step === "success") {
      const duration = 3 * 1000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 100 };

      const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

      const interval: any = setInterval(function() {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);
        confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
        confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
      }, 250);

      return () => clearInterval(interval);
    }
  }, [step]);

  const handleNextStep = () => {
    if (step === "selection") setStep("details");
    else if (step === "details") setStep("payment");
  };

  const handleBackStep = () => {
    if (step === "details") setStep("selection");
    else if (step === "payment") setStep("details");
  };

  const handlePayment = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setStep("success");
    }, 2000);
  };

  const isDetailsValid = formData.name && formData.email && formData.phone;

  return (
    <AnimatePresence>
      {stylist && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-ink/60 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 220 }}
            className="fixed right-0 top-0 z-50 h-full w-full max-w-xl overflow-y-auto bg-card shadow-editorial"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card/95 px-8 py-5 backdrop-blur">
              <div className="flex items-center gap-4">
                {step !== "selection" && step !== "success" && (
                  <button
                    onClick={handleBackStep}
                    className="flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-secondary"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                )}
                <div>
                  <p className="eyebrow">
                    {step === "selection" && "Reservation"}
                    {step === "details" && "Your Information"}
                    {step === "payment" && "Secure Payment"}
                    {step === "success" && "Confirmed"}
                  </p>
                  <h3 className="mt-1 text-2xl">
                    {step === "success" ? "Booking Successful" : stylist.name}
                  </h3>
                </div>
              </div>
              <button
                onClick={onClose}
                aria-label="Close"
                className="flex h-10 w-10 items-center justify-center border border-border transition-colors hover:bg-secondary"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="px-8 py-8">
              <AnimatePresence mode="wait">
                {step === "selection" && (
                  <motion.div
                    key="selection"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    {/* Stylist preview */}
                    <div className="flex items-center gap-4 border-b border-border pb-6">
                      <Image
                        src={stylist.image}
                        alt={stylist.name}
                        className="h-20 w-20 object-cover grayscale"
                      />
                      <div>
                        <p className="eyebrow">{stylist.specialty}</p>
                        <p className="mt-1 font-display text-lg">{stylist.service}</p>
                      </div>
                    </div>

                    {/* Date */}
                    <div className="mt-8">
                      <div className="mb-4 flex items-center gap-2">
                        <CalendarIcon className="h-4 w-4 text-rose-gold" />
                        <p className="eyebrow">Select Date</p>
                      </div>
                      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                        {days.map((d) => {
                          const active = d.toDateString() === selectedDate.toDateString();
                          return (
                            <button
                              key={d.toISOString()}
                              onClick={() => setSelectedDate(d)}
                              className={`flex min-w-[64px] flex-col items-center border px-3 py-3 transition-all ${active
                                ? "border-ink bg-ink text-nude"
                                : "border-border hover:border-rose-gold"
                                }`}
                            >
                              <span className="text-[0.6rem] uppercase tracking-editorial opacity-70">
                                {d.toLocaleDateString("en", { weekday: "short" })}
                              </span>
                              <span className="mt-1 font-display text-xl">
                                {d.getDate()}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Time slots */}
                    <div className="mt-8 space-y-6">
                      {Object.entries(slots).map(([period, times]) => (
                        <div key={period}>
                          <p className="eyebrow mb-3">{period}</p>
                          <div className="grid grid-cols-3 gap-2">
                            {times.map((t) => {
                              const active = selectedTime === t;
                              return (
                                <button
                                  key={t}
                                  onClick={() => setSelectedTime(t)}
                                  className={`border py-3 text-sm tracking-wider transition-all ${active
                                    ? "border-rose-gold bg-rose-gold text-nude"
                                    : "border-border hover:border-ink"
                                    }`}
                                >
                                  {t}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {step === "details" && (
                  <motion.div
                    key="details"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-6"
                  >
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="eyebrow">Full Name</Label>
                        <Input
                          id="name"
                          placeholder="Your Name"
                          className="rounded-none border-border bg-transparent focus-visible:ring-ink"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="eyebrow">Email Address</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="email@example.com"
                          className="rounded-none border-border bg-transparent focus-visible:ring-ink"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="eyebrow">Phone Number</Label>
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="+55 (11) 00000-0000"
                          className="rounded-none border-border bg-transparent focus-visible:ring-ink"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="bg-secondary/50 p-6">
                      <p className="eyebrow mb-3">Appointment Summary</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Service</span>
                          <span>{stylist.service}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Professional</span>
                          <span>{stylist.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Date</span>
                          <span>{selectedDate.toLocaleDateString("en", { weekday: 'long', month: 'long', day: 'numeric' })}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Time</span>
                          <span>{selectedTime}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {step === "payment" && (
                  <motion.div
                    key="payment"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-8"
                  >
                    <div className="space-y-4">
                      <p className="eyebrow flex items-center gap-2">
                        <Lock className="h-3 w-3" /> Secure Payment Simulation
                      </p>
                      
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex h-20 items-center justify-center border border-ink bg-ink text-nude">
                            <Apple className="h-6 w-6" />
                          </div>
                          <div className="flex h-20 items-center justify-center border border-border">
                            <CreditCard className="h-6 w-6 opacity-40" />
                          </div>
                        </div>
                        
                        <div className="rounded-none border border-border p-6 text-center">
                          <p className="text-sm text-muted-foreground">
                            This is a secure checkout simulation.
                            Click the button below to complete your reservation.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-border pt-6">
                      <div className="flex justify-between">
                        <p className="eyebrow">Total Due</p>
                        <p className="font-display text-3xl">{formatPrice(stylist.price)}</p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {step === "success" && (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center py-10 text-center"
                  >
                    <div className="mb-6 rounded-full bg-green-50 p-4">
                      <CheckCircle2 className="h-16 w-16 text-green-600" />
                    </div>
                    <h2 className="mb-2 font-display text-4xl">Congratulations, {formData.name.split(" ")[0]}!</h2>
                    <p className="mb-10 text-muted-foreground">Your reservation has been confirmed.</p>
                    
                    <div className="w-full space-y-4 border border-border p-8 text-left">
                      <div className="flex items-center gap-4 border-b border-border pb-6">
                        <Image
                          src={stylist.image}
                          alt={stylist.name}
                          className="h-16 w-16 object-cover grayscale"
                        />
                        <div>
                          <p className="eyebrow">{stylist.specialty}</p>
                          <p className="font-display text-lg">{stylist.name}</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-y-6 pt-2">
                        <div>
                          <p className="eyebrow">Service</p>
                          <p className="mt-1 text-sm">{stylist.service}</p>
                        </div>
                        <div>
                          <p className="eyebrow">Price</p>
                          <p className="mt-1 text-sm">{formatPrice(stylist.price)}</p>
                        </div>
                        <div>
                          <p className="eyebrow">Date</p>
                          <p className="mt-1 text-sm">
                            {selectedDate.toLocaleDateString("en", {
                              month: "short",
                              day: "numeric",
                              year: "numeric"
                            })}
                          </p>
                        </div>
                        <div>
                          <p className="eyebrow">Time</p>
                          <p className="mt-1 text-sm">{selectedTime}</p>
                        </div>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={onClose}
                      className="mt-10 h-14 w-full rounded-none bg-ink text-nude hover:bg-rose-gold"
                    >
                      Close and Return
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Summary footer */}
            {step !== "success" && (
              <div className="sticky bottom-0 border-t border-border bg-card/95 px-8 py-5 backdrop-blur">
                <div className="flex items-end justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="eyebrow">Summary</p>
                    <p className="mt-1 truncate text-sm">
                      {stylist.service} · {stylist.name.split(" ")[0]}
                      {selectedTime && (
                        <span className="text-muted-foreground">
                          {" "}
                          · {selectedDate.toLocaleDateString("en", {
                            month: "short",
                            day: "numeric",
                          })}{" "}
                          {selectedTime}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="eyebrow">Total</p>
                    <p className="font-display text-2xl">{formatPrice(stylist.price)}</p>
                  </div>
                </div>
                
                <button
                  onClick={step === "payment" ? handlePayment : handleNextStep}
                  disabled={
                    (step === "selection" && !selectedTime) || 
                    (step === "details" && !isDetailsValid) ||
                    isProcessing
                  }
                  className="mt-4 flex w-full items-center justify-center gap-2 bg-ink py-4 text-[0.7rem] font-medium uppercase tracking-editorial text-nude transition-all hover:bg-rose-gold disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      {step === "selection" && (selectedTime ? "Reserve Your Spot" : "Select a time")}
                      {step === "details" && "Continue to Payment"}
                      {step === "payment" && "Complete Reservation"}
                    </>
                  )}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
